/**
 * Pricing rule constants and helpers.
 *
 * Single source of truth for the synthetic pricing fieldIds, shared by the
 * admin bundle (which writes them) and the frontend bundle (which evaluates
 * them). Must stay in sync with PHP `Pricing_Logic`.
 *
 * The IDs deliberately avoid `::` — unlike user rules, pricing rules keep their
 * fieldId all the way into GF's evaluation, where it is interpolated into a
 * jQuery selector. A `::` there reads as a CSS pseudo-element and throws.
 */
import { __ } from '@wordpress/i18n';

/** Prefix shared by every pricing fieldId. */
export const PRICING_PREFIX = 'gpacl_pricing_';

/** fieldId prefix for a Product field's unit price. Append the field ID. */
export const PRICING_PRICE_PREFIX = 'gpacl_pricing_price_';

/** fieldId prefix for a Product field's quantity. Append the field ID. */
export const PRICING_QUANTITY_PREFIX = 'gpacl_pricing_quantity_';

/** fieldId for the order subtotal. */
export const PRICING_SUBTOTAL_ID = 'gpacl_pricing_subtotal';

/** fieldId for the order tax. */
export const PRICING_TAX_ID = 'gpacl_pricing_tax';

/** Operators offered for pricing rules. All pricing values are numeric. */
export const PRICING_OPERATORS: Record<string, string> = {
	is: 'is',
	isnot: 'isNot',
	'>': 'greaterThan',
	'>=': '>=',
	'<': 'lessThan',
	'<=': '<=',
};

/** Whether a rule reads a pricing value. */
export function isPricingMode(rule: { fieldId: string | number }): boolean {
	return String(rule.fieldId).indexOf(PRICING_PREFIX) === 0;
}

/**
 * Extract the Product field ID from a product-scoped pricing fieldId.
 *
 * @param fieldId The pricing fieldId.
 * @return The Product field ID, or 0 for order-level IDs (subtotal, tax).
 */
export function pricingProductFieldId(fieldId: string | number): number {
	const id = String(fieldId);

	for (const prefix of [PRICING_PRICE_PREFIX, PRICING_QUANTITY_PREFIX]) {
		if (id.indexOf(prefix) === 0) {
			return parseInt(id.slice(prefix.length), 10) || 0;
		}
	}

	return 0;
}

/** Label for the "Pricing" optgroup in the field dropdown. */
export function pricingGroupLabel(): string {
	return __('Pricing', 'gp-advanced-conditional-logic');
}
